// One personal setting, in the existing profile JSON. No browser-local preference store.
import { useEffect, useSyncExternalStore } from 'react';
import { supabaseClient } from '../config/supabase';

export const normalizeSelfHelp = value => typeof value === 'boolean' ? value : true;
export async function loadSelfHelpPreference(client, userId) {
    const { data, error } = await client.from('user_profiles').select('settings').eq('id', userId).maybeSingle();
    if (error) throw error;
    return normalizeSelfHelp(data?.settings?.ui?.self_help_enabled);
}
export async function saveSelfHelpPreference(client, enabled) {
    const { error } = await client.rpc('set_my_self_help', { p_enabled: enabled });
    if (error) throw error;
    return enabled;
}

// Shared by all mounted help buttons and the settings card. A single profile read per
// person/client; optimistic changes and rollbacks reach every button immediately.
const clients = new WeakMap();
function preferenceStore(client, userId) {
    if (!clients.has(client)) clients.set(client, new Map());
    const people = clients.get(client);
    if (!people.has(userId)) {
        let state = { enabled: true, loaded: !userId, saving: false };
        let started = false;
        const listeners = new Set();
        const publish = patch => { state = { ...state, ...patch }; listeners.forEach(fn => fn()); };
        people.set(userId, {
            subscribe: fn => { listeners.add(fn); return () => listeners.delete(fn); },
            snapshot: () => state,
            load: () => {
                if (started || !userId) return;
                started = true;
                loadSelfHelpPreference(client, userId)
                    .then(enabled => publish({ enabled, loaded: true }))
                    .catch(() => { started = false; publish({ loaded: true }); });
            },
            save: async enabled => {
                if (!userId || state.saving) return;
                const previous = state.enabled;
                publish({ enabled, saving: true });
                try { await saveSelfHelpPreference(client, enabled); }
                catch (error) { publish({ enabled: previous }); throw error; }
                finally { publish({ saving: false }); }
            },
        });
    }
    return people.get(userId);
}

// HelpButton/HelpTip callers need no user prop: they follow the signed-in person.
// Subscribe once while help is mounted; account changes never reuse another person's store.
let authUserId = null;
const authListeners = new Set();
let authSubscription;
let authGeneration = 0;
function subscribeAuth(listener) {
    authListeners.add(listener);
    if (authListeners.size === 1) {
        const generation = ++authGeneration;
        const receive = session => {
            if (generation !== authGeneration) return;
            authUserId = session?.user?.id || null;
            authListeners.forEach(fn => fn());
        };
        let authEventReceived = false;
        authSubscription = supabaseClient.auth?.onAuthStateChange?.((_event, session) => {
            authEventReceived = true;
            receive(session);
        })?.data?.subscription;
        supabaseClient.auth?.getSession?.().then(({ data }) => {
            if (!authEventReceived) receive(data?.session);
        }).catch(() => {});
    }
    return () => {
        authListeners.delete(listener);
        if (!authListeners.size) { ++authGeneration; authSubscription?.unsubscribe(); authSubscription = undefined; authUserId = null; }
    };
}
const authSnapshot = () => authUserId;
export function useSelfHelpPreference(userId, client = supabaseClient) {
    const signedInUserId = useSyncExternalStore(subscribeAuth, authSnapshot, () => null);
    const store = preferenceStore(client, userId || signedInUserId);
    const state = useSyncExternalStore(store.subscribe, store.snapshot, store.snapshot);
    useEffect(() => { store.load(); }, [store]);
    return { ...state, setEnabled: store.save };
}
